import { useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { buildColorMap, contrastTextColor } from "./colors";
import {
  NormalizedFloor,
  TiledFloor,
  TiledFloorEntry,
  TiledView,
  TiledViewsData,
} from "./types";

interface TiledViewsWindowProps {
  open: boolean;
  onClose: () => void;
  /** Dataset, fetched by the parent the same way plan info is fetched. */
  data: TiledViewsData | null;
  loading?: boolean;
  error?: string | null;
  /** Title shown in the window header. */
  title?: string;
  /** Secondary line under the title (e.g. property name / number). */
  subtitle?: string;
  /** Currently loaded floor (e.g. "840S-LP1"), highlighted in the list. */
  activeFloorName?: string;
  /** Called when a floor row is clicked. */
  onSelectFloor: (floorName: string, planName: string) => void;
}

/** Detect the function array inside a floor (companies, roomTypes, ...). */
function getFloorEntries(floor: TiledFloor): TiledFloorEntry[] {
  for (const key of Object.keys(floor)) {
    if (key === "floorName" || key === "floorPlan") continue;
    const value = floor[key];
    if (Array.isArray(value)) return value as TiledFloorEntry[];
  }
  return [];
}

/** "840S-LP1" -> "LP1" (segment after the last dash). */
function derivePlanName(floorName: string): string {
  const idx = floorName.lastIndexOf("-");
  return idx >= 0 ? floorName.substring(idx + 1) : floorName;
}

/** "840S-LP1" -> "1" (trailing number, for the level index gutter). */
function deriveLevelLabel(floorName: string): string {
  const m = floorName.match(/(\d+)\s*$/);
  return m ? m[1] : derivePlanName(floorName);
}

function parsePercentage(raw: string): number {
  const n = parseFloat(String(raw).replace("%", "").trim());
  return Number.isFinite(n) ? n : 0;
}

/** Natural sort so LP2 comes before LP10. */
function compareFloorNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

const TECH_FONT =
  'ui-monospace, "SF Mono", "JetBrains Mono", "Roboto Mono", Menlo, Consolas, monospace';

const TiledViewsWindow = ({
  open,
  onClose,
  data,
  loading = false,
  error = null,
  title = "Tower Overview",
  subtitle,
  activeFloorName,
  onSelectFloor,
}: TiledViewsWindowProps) => {
  const [activeViewIndex, setActiveViewIndex] = useState<number>(0);
  // Function name to spotlight across all floors (hover = preview, click = lock).
  const [hoverName, setHoverName] = useState<string | null>(null);
  const [lockedName, setLockedName] = useState<string | null>(null);
  const focusedName = hoverName ?? lockedName;
  const nodeRef = useRef<HTMLDivElement>(null);

  const views: TiledView[] = data?.views ?? [];
  const currentView: TiledView | undefined = views[activeViewIndex];

  // Color map for the names present in the current view.
  const colorMap = useMemo(() => {
    if (!currentView) return {} as Record<string, string>;
    const names: string[] = [];
    currentView.floors.forEach((floor) =>
      getFloorEntries(floor).forEach((entry) => names.push(entry.name))
    );
    return buildColorMap(names);
  }, [currentView]);

  // Normalize floors, sorted DESCENDING so the tower reads like an elevation
  // (highest level on top, ground level at the bottom).
  const floors: NormalizedFloor[] = useMemo(() => {
    if (!currentView) return [];
    return currentView.floors
      .map((floor) => ({
        floorName: floor.floorName,
        floorPlan: floor.floorPlan,
        planName: derivePlanName(floor.floorName),
        segments: getFloorEntries(floor).map((entry) => ({
          name: entry.name,
          percentage: parsePercentage(entry.percentage),
          color: colorMap[entry.name] || "#cbd5e1",
        })),
      }))
      .sort((a, b) => compareFloorNames(b.floorName, a.floorName));
  }, [currentView, colorMap]);

  // Legend entries with the floor count each function appears on.
  const legend = useMemo(() => {
    const counts: Record<string, number> = {};
    floors.forEach((f) =>
      f.segments.forEach((s) => {
        counts[s.name] = (counts[s.name] || 0) + 1;
      })
    );
    return Object.keys(colorMap).map((name) => ({
      name,
      color: colorMap[name],
      count: counts[name] || 0,
    }));
  }, [colorMap, floors]);

  if (!open) return null;

  const toggleLock = (name: string) =>
    setLockedName((cur) => (cur === name ? null : name));

  return (
    <Draggable
      handle=".tiled-views-drag-handle"
      nodeRef={nodeRef}
      defaultPosition={{ x: 72, y: 64 }}
      bounds="body"
    >
      <div
        ref={nodeRef}
        className="tiled-views-window fixed top-0 left-0 flex flex-col rounded-xl overflow-hidden"
        style={{
          zIndex: 10000002,
          width: 640,
          maxWidth: "96vw",
          maxHeight: "88vh",
          background: "#ffffff",
          border: "1px solid #e3e6ea",
          boxShadow:
            "0 24px 60px -20px rgba(15,40,70,0.35), 0 2px 8px rgba(15,40,70,0.12)",
        }}
      >
        <style>{`
          @keyframes tvRowIn {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes tvBarGrow {
            from { transform: scaleX(0); }
            to   { transform: scaleX(1); }
          }
          .tiled-views-window .tv-row { animation: tvRowIn .42s cubic-bezier(.22,.61,.36,1) both; }
          .tiled-views-window .tv-bar { transform-origin: left center; animation: tvBarGrow .55s cubic-bezier(.22,.61,.36,1) both; }
          .tiled-views-window .tv-scroll::-webkit-scrollbar { width: 9px; }
          .tiled-views-window .tv-scroll::-webkit-scrollbar-thumb { background: #cfd6de; border-radius: 9px; border: 2px solid #fff; }
          .tiled-views-window .tv-scroll::-webkit-scrollbar-track { background: transparent; }
        `}</style>

        {/* ── Header / drag handle ── */}
        <div
          className="tiled-views-drag-handle cursor-move select-none px-4 py-3 flex items-start justify-between gap-3"
          style={{
            background:
              "linear-gradient(135deg, var(--tv-h1,#0f3557) 0%, var(--tv-h2,#1b4f7e) 100%)",
            color: "#fff",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="grid place-items-center rounded-md shrink-0"
              style={{ width: 34, height: 34, background: "rgba(255,255,255,0.14)" }}
            >
              {/* stacked-floors / tower mark */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="3" width="16" height="4" rx="1" opacity="0.55" />
                <rect x="4" y="10" width="16" height="4" rx="1" opacity="0.8" />
                <rect x="4" y="17" width="16" height="4" rx="1" />
              </svg>
            </span>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold leading-tight truncate">{title}</h2>
              {subtitle && (
                <p className="text-[11px] leading-tight truncate" style={{ color: "rgba(255,255,255,0.72)" }}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 transition-colors shrink-0"
            style={{ background: "rgba(255,255,255,0.10)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.24)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.10)")}
            aria-label="Close tower overview"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Toolbar: view switcher + floor count ── */}
        {views.length > 0 && (
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b" style={{ borderColor: "#eef1f4", background: "#fafbfc" }}>
            <div className="inline-flex rounded-lg p-0.5" style={{ background: "#eef1f4" }}>
              {views.map((view, idx) => (
                <button
                  key={view.view}
                  type="button"
                  onClick={() => {
                    setActiveViewIndex(idx);
                    setLockedName(null);
                    setHoverName(null);
                  }}
                  className="text-xs font-medium rounded-md px-3 py-1.5 transition-all"
                  style={
                    idx === activeViewIndex
                      ? { background: "#fff", color: "#0f3557", boxShadow: "0 1px 3px rgba(15,40,70,0.18)" }
                      : { background: "transparent", color: "#64748b" }
                  }
                >
                  {view.view}
                </button>
              ))}
            </div>
            <span className="text-[11px] tracking-wide" style={{ fontFamily: TECH_FONT, color: "#94a3b8" }}>
              {floors.length} LEVELS
            </span>
          </div>
        )}

        {/* ── Body: tower elevation ── */}
        <div
          className="tv-scroll flex-1 overflow-y-auto px-4 py-3"
          style={{
            backgroundColor: "#f7f9fb",
            backgroundImage:
              "linear-gradient(#e9eef3 1px, transparent 1px), linear-gradient(90deg, #e9eef3 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        >
          {loading && (
            <div className="space-y-2 py-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-16 h-6 rounded bg-gray-200" />
                  <div className="flex-1 h-7 rounded bg-gray-200" />
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="py-8 text-center">
              <p className="text-sm font-medium text-red-600">Could not load the tower data</p>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: TECH_FONT }}>{error}</p>
            </div>
          )}

          {!loading && !error && floors.length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center">No floors to display.</p>
          )}

          {!loading && !error && floors.length > 0 && (
            <div className="relative">
              {/* roof cap */}
              <div className="flex items-center gap-3 mb-1 pl-[4.5rem]">
                <div className="flex-1 border-t-2 border-dashed" style={{ borderColor: "#cbd5e1" }} />
                <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: TECH_FONT, color: "#94a3b8" }}>roof</span>
                <div className="flex-1 border-t-2 border-dashed" style={{ borderColor: "#cbd5e1" }} />
              </div>

              {floors.map((floor, idx) => {
                const isActive = activeFloorName === floor.floorName;
                const hasFocused =
                  !focusedName || floor.segments.some((s) => s.name === focusedName);
                return (
                  <button
                    key={floor.floorName}
                    type="button"
                    title={`Open ${floor.floorName}`}
                    onClick={() => onSelectFloor(floor.floorName, floor.planName)}
                    className="tv-row group w-full text-left flex items-stretch gap-0 mb-[3px] rounded-lg transition-all"
                    style={{
                      animationDelay: `${Math.min(idx * 28, 420)}ms`,
                      opacity: hasFocused ? 1 : 0.55,
                      outline: isActive ? "2px solid #e8590c" : "1px solid transparent",
                      background: isActive ? "rgba(232,89,12,0.06)" : "transparent",
                    }}
                  >
                    {/* level gutter */}
                    <span
                      className="shrink-0 w-[4.5rem] flex flex-col items-start justify-center pr-2 py-1"
                      style={{ fontFamily: TECH_FONT }}
                    >
                      <span
                        className="text-[12px] font-semibold leading-none"
                        style={{ color: isActive ? "#c2410c" : "#334155" }}
                      >
                        {floor.planName}
                      </span>
                      <span className="text-[9px] leading-none mt-0.5" style={{ color: "#94a3b8" }}>
                        L{deriveLevelLabel(floor.floorName)}
                      </span>
                    </span>

                    {/* the floor slab (stacked bar) */}
                    <span
                      className="tv-bar relative flex-1 flex h-8 rounded-md overflow-hidden"
                      style={{
                        animationDelay: `${Math.min(idx * 28, 420) + 90}ms`,
                        border: "1px solid rgba(15,40,70,0.10)",
                        boxShadow: isActive
                          ? "0 2px 6px rgba(232,89,12,0.22)"
                          : "0 1px 2px rgba(15,40,70,0.06)",
                      }}
                    >
                      {floor.segments.map((seg, sIdx) => {
                        const dim = focusedName && seg.name !== focusedName;
                        const hot = focusedName && seg.name === focusedName;
                        return (
                          <span
                            key={floor.floorName + "-" + seg.name + "-" + sIdx}
                            title={`${seg.name} · ${seg.percentage}%`}
                            onMouseEnter={() => setHoverName(seg.name)}
                            onMouseLeave={() => setHoverName(null)}
                            className="h-full flex items-center justify-center overflow-hidden whitespace-nowrap transition-all duration-200"
                            style={{
                              width: `${seg.percentage}%`,
                              backgroundColor: seg.color,
                              color: contrastTextColor(seg.color),
                              fontSize: 10,
                              fontFamily: TECH_FONT,
                              opacity: dim ? 0.22 : 1,
                              filter: dim ? "grayscale(0.5)" : "none",
                              boxShadow: hot ? "inset 0 0 0 2px rgba(15,40,70,0.55)" : "none",
                              borderRight:
                                sIdx < floor.segments.length - 1
                                  ? "1px solid rgba(255,255,255,0.35)"
                                  : "none",
                            }}
                          >
                            {seg.percentage >= 20
                              ? `${seg.name} ${Math.round(seg.percentage)}%`
                              : seg.percentage >= 9
                              ? `${Math.round(seg.percentage)}%`
                              : ""}
                          </span>
                        );
                      })}

                      {/* hover affordance + active badge */}
                      {isActive && (
                        <span
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: "#e8590c", color: "#fff", fontFamily: TECH_FONT }}
                        >
                          viewing
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}

              {/* ground line */}
              <div className="flex items-center gap-3 mt-1 pl-[4.5rem]">
                <div className="flex-1 border-t-2" style={{ borderColor: "#64748b" }} />
                <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: TECH_FONT, color: "#64748b" }}>ground</span>
                <div className="flex-1 border-t-2" style={{ borderColor: "#64748b" }} />
              </div>
            </div>
          )}
        </div>

        {/* ── Interactive legend ── */}
        {!loading && !error && legend.length > 0 && (
          <div className="border-t" style={{ borderColor: "#eef1f4", background: "#fafbfc" }}>
            <div className="flex items-center justify-between px-4 pt-2">
              <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: TECH_FONT, color: "#94a3b8" }}>
                {currentView?.view}
              </span>
              {lockedName && (
                <button
                  type="button"
                  onClick={() => setLockedName(null)}
                  className="text-[10px] font-medium text-primary-700 hover:underline"
                  style={{ fontFamily: TECH_FONT }}
                >
                  clear focus ✕
                </button>
              )}
            </div>
            <div className="px-3 pb-2 pt-1.5 flex flex-wrap gap-1 max-h-24 overflow-y-auto tv-scroll">
              {legend.map((item) => {
                const active = focusedName === item.name;
                return (
                  <button
                    key={item.name}
                    type="button"
                    onMouseEnter={() => setHoverName(item.name)}
                    onMouseLeave={() => setHoverName(null)}
                    onClick={() => toggleLock(item.name)}
                    className="flex items-center gap-1.5 rounded-full pl-1 pr-2 py-0.5 transition-all"
                    style={{
                      border: `1px solid ${active ? "#0f3557" : "#e2e8f0"}`,
                      background: active ? "#0f3557" : "#fff",
                      color: active ? "#fff" : "#475569",
                    }}
                    title={`${item.count} level${item.count > 1 ? "s" : ""}`}
                  >
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: item.color, boxShadow: "0 0 0 1px rgba(0,0,0,0.06)" }} />
                    <span className="text-[11px]">{item.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="px-4 pb-2">
              <p className="text-[10px]" style={{ color: "#94a3b8" }}>
                Click a level to open its floor plan · hover or click a tag to focus a function
              </p>
            </div>
          </div>
        )}
      </div>
    </Draggable>
  );
};

export default TiledViewsWindow;
